import { useState } from 'react';
import { useLocation } from 'react-router';
import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Tabs from '@/design_system/Tabs';
import { Tab, TabList, TabPanel } from '@/design_system/Tabs/Tabs';
import Title from '@/design_system/Typography/Title';
import IconChevron from '@/icons/Chevron.svg';
import { useCurrentSession } from '@/services/auth';
import useViewport from '@/utils/useViewport';

import { InformationTab } from './Tabs/InformationTab';
import { NotificationsTab } from './Tabs/NotificationsTab/NotificationsTab';

const tabs = ['information', 'notifications'];
const isValidTab = (tab: string | null): tab is Tab => !!tab && tabs.includes(tab);

type Tab = (typeof tabs)[number];

const Profile = () => {
  const { isMobile } = useViewport();
  const { currentSession, setCurrentSession } = useCurrentSession();
  const location = useLocation();
  const tabQueryParam = new URLSearchParams(location.search).get('tab');

  const initialTab = isValidTab(tabQueryParam) ? tabQueryParam : 'information';
  const [tab, setTab] = useState<Tab>(initialTab);

  return (
    <>
      <div className="header">
        <Title size="200" as="h1">
          <Stack row alignItems="center" gap="1rem">
            {isMobile && (
              <Button iconOnly variant="secondary" to="/menu">
                <IconChevron left />
              </Button>
            )}
            <Trans id="profile.title">Profile</Trans>
          </Stack>
        </Title>
      </div>

      <div className="container container--list">
        <Tabs
          gap="24px"
          selectedKey={tab}
          onSelectionChange={(tab) => {
            setTab(isValidTab(tab) ? tab : 'information');
          }}
          style={{ flex: 1, paddingBottom: 24 }}
        >
          <TabList>
            <Tab id={'information' satisfies Tab}>
              <Trans id="profile.tabs.information">Information</Trans>
            </Tab>

            <Tab id={'notifications' satisfies Tab}>
              <Trans id="profile.tabs.notification">Notifications</Trans>
            </Tab>
          </TabList>

          <TabPanel id={'information' satisfies Tab} style={{ flex: 1 }}>
            <InformationTab currentSession={currentSession} setCurrentSession={setCurrentSession} />
          </TabPanel>

          <TabPanel id={'notifications' satisfies Tab} style={{ flex: 1 }}>
            <NotificationsTab user={currentSession!} />
          </TabPanel>
        </Tabs>
      </div>
    </>
  );
};

export default Profile;
